/**
 * MP Shipping Dates (PDP)
 * ADDED: 2026-07-30 by MeshPlanet
 *
 * <mp-shipping-dates> shows when the product leaves the warehouse and when it
 * lands. Both dates are counted from today in business days and are the same
 * for every variant — the extra production time a monogrammed bag needs is
 * stated in the block's disclaimer instead of moving the dates.
 *
 * Liquid renders the dates so the card is never blank, and this element
 * re-derives them on load, because a CDN-cached page can otherwise keep serving
 * yesterday's dates.
 *
 * The business-day walk is mirrored in snippets/mp-business-day-epoch.liquid.
 * Keep the two in step.
 *
 * Data attributes:
 *   data-today          shop date at render, YYYY-MM-DD — floor for the clock-derived date
 *   data-utc-offset     shop UTC offset at render, ±HHMM
 *   data-ship-days      business days before the order ships
 *   data-delivery-days  business days in transit, counted from the ship date
 *   data-working-days   comma separated weekday numbers the shop ships on, 0 = Sunday
 *   data-holidays       non-shipping dates as YYYY-MM-DD
 *   data-date-order     part order handed to the formatter
 */
(function () {
  'use strict';

  /* Long enough for any count the block allows, short enough that a malformed
     holiday list cannot spin. */
  var MAX_STEPS = 400;

  /** @param {Date} date @returns {string} */
  function toIsoDate(date) {
    var month = date.getMonth() + 1;
    var day = date.getDate();

    return date.getFullYear() + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day;
  }

  /** @param {string} value @returns {Date | null} */
  function fromIsoDate(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());

    if (!match) return null;

    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  /**
   * Shop UTC offset as minutes, from the ±HHMM strftime form.
   *
   * @param {string | undefined} value
   * @returns {number | null}
   */
  function parseUtcOffset(value) {
    var match = /^([+-])(\d{2})(\d{2})$/.exec(String(value || '').trim());

    if (!match) return null;

    var minutes = Number(match[2]) * 60 + Number(match[3]);

    return match[1] === '-' ? -minutes : minutes;
  }

  /**
   * @param {string | undefined} value
   * @param {number} fallback
   * @returns {number}
   */
  function toCount(value, fallback) {
    var parsed = parseInt(String(value), 10);

    return isNaN(parsed) || parsed < 0 ? fallback : parsed;
  }

  /**
   * @param {string} locale
   * @param {Date} date
   * @param {Intl.DateTimeFormatOptions} options
   * @param {string} fallback
   * @returns {string}
   */
  function formatPart(locale, date, options, fallback) {
    try {
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch (error) {
      return fallback;
    }
  }

  class MpShippingDates extends HTMLElement {
    connectedCallback() {
      /** @type {HTMLElement | null} */
      this.shipDate = this.querySelector('[data-mp-ship-date]');
      /** @type {HTMLElement | null} */
      this.deliveryDate = this.querySelector('[data-mp-delivery-date]');

      if (!this.shipDate || !this.deliveryDate) return;

      this.render();
    }

    /**
     * The shop's calendar day, not the visitor's. Derived from the clock plus
     * the shop offset so a cached page cannot freeze it, and floored at the date
     * Liquid rendered, since a cached page can only ever be the older of the two.
     *
     * @returns {Date}
     */
    get today() {
      var rendered = fromIsoDate(this.dataset.today || '');
      var offset = parseUtcOffset(this.dataset.utcOffset);
      var derived = null;

      if (offset !== null) {
        var now = new Date();
        var shopNow = new Date(now.getTime() + (now.getTimezoneOffset() + offset) * 60000);

        derived = new Date(shopNow.getFullYear(), shopNow.getMonth(), shopNow.getDate());
      }

      if (derived && rendered) return derived > rendered ? derived : rendered;
      if (derived) return derived;
      if (rendered) return rendered;

      var local = new Date();

      return new Date(local.getFullYear(), local.getMonth(), local.getDate());
    }

    /** @returns {number[]} */
    get workingDays() {
      var days = String(this.dataset.workingDays || '1,2,3,4,5')
        .split(',')
        .map(function (day) {
          return parseInt(day.trim(), 10);
        })
        .filter(function (day) {
          return day >= 0 && day <= 6;
        });

      return days.length ? days : [1, 2, 3, 4, 5];
    }

    /**
     * @param {Date} from
     * @param {number} count
     * @returns {Date}
     */
    addBusinessDays(from, count) {
      var working = this.workingDays;
      var holidays = String(this.dataset.holidays || '');
      /* setDate() walks calendar days, so a DST change cannot swallow one. */
      var cursor = new Date(from.getTime());
      var remaining = count;
      var steps = 0;

      while (remaining > 0 && steps < MAX_STEPS) {
        steps++;
        cursor.setDate(cursor.getDate() + 1);

        if (working.indexOf(cursor.getDay()) === -1) continue;
        if (holidays && holidays.indexOf(toIsoDate(cursor)) !== -1) continue;

        remaining--;
      }

      return cursor;
    }

    /**
     * @param {Date} date
     * @returns {string}
     */
    format(date) {
      var locale = document.documentElement.lang || 'en';
      var weekday = formatPart(locale, date, { weekday: 'short' }, '');
      var month = formatPart(locale, date, { month: 'short' }, '');
      var day = String(date.getDate());

      switch (this.dataset.dateOrder) {
        case 'weekday_month_day':
          return (weekday + ' ' + month + ' ' + day).trim();
        case 'day_month':
          return (day + ' ' + month).trim();
        case 'month_day':
          return (month + ' ' + day).trim();
        default:
          return (weekday + ' ' + day + ' ' + month).trim();
      }
    }

    render() {
      if (!this.shipDate || !this.deliveryDate) return;

      var ships = this.addBusinessDays(this.today, toCount(this.dataset.shipDays, 1));
      var delivers = this.addBusinessDays(ships, toCount(this.dataset.deliveryDays, 4));

      this.shipDate.textContent = this.format(ships);
      this.deliveryDate.textContent = this.format(delivers);
    }
  }

  if (!customElements.get('mp-shipping-dates')) {
    customElements.define('mp-shipping-dates', MpShippingDates);
  }
})();
